import { action$, ofType, spreadMap } from '@ardoq/rxbeach';
import type {
  ContextShape,
  GraphModelShape,
  LoadedGraphWithViewpointMode,
} from '@ardoq/data-model';
import type { I18nLocale } from '@ardoq/i18n';
import { mainAppModuleSidebar$ } from 'appContainer/MainAppModule/MainAppModuleSidebar/mainAppModuleSidebar$';
import { loadedScenarioData$ } from 'loadedScenarioData$';
import { graphModel$ } from 'modelInterface/graphModel$';
import { nonComponentModelUpdateActions } from 'modelInterface/modelUpdateNotification$';
import {
  components$,
  type ComponentsState,
  componentParentChange$,
  componentSetChanged$,
  scenarioRelatedComponentIds$,
  type ScenarioRelatedComponentIds,
} from 'streams/components/components$';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  startWith,
} from 'rxjs';
import { scenarioRelatedComponents$ } from 'scenarioRelated/scenarioRelatedComponents$';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { context$ } from 'streams/context/context$';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { relationshipDiagramReset } from '../actions';
import { startAction } from 'actions/utils';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { isUpdatingAppState$ } from 'isUpdatingAppState$';
import { GlobalRelationshipsViewModelResetStreamShape } from '../viewModel$/types';
import { layoutUpdate$ } from 'tabview/graphComponent/layoutUpdate$';
import { scenarios$ } from 'streams/scenarios/scenarios$';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { isEqual } from 'lodash';
import { models$ } from 'streams/models/models$';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { usersOperations } from '@ardoq/core';
import { i18nLocale$ } from 'streams/i18nLocale$';

export const scenario$ = combineLatest([
  loadedScenarioData$,
  scenarioRelatedComponents$,
  mainAppModuleSidebar$,
  scopeDiff$,
]);

const relationshipsDiagramModelUpdateReset$ = combineLatest([
  merge(
    action$.pipe(
      ofType(...nonComponentModelUpdateActions, relationshipDiagramReset)
    ),
    componentSetChanged$,
    componentParentChange$
  ).pipe(startWith(startAction()), debounceTime(DEFAULT_DEBOUNCE_TIME)),
  models$,
]);

const isNotUpdatingAppState = <T extends [boolean, ...unknown[]]>(
  tuple: T
): boolean => !tuple[0];

const toGlobalViewModelResetPayload = (
  _isUpdatingAppState: boolean,
  context: ContextShape,
  components: ComponentsState,
  scenarioRelatedComponentIds: ScenarioRelatedComponentIds,
  graphModel: GraphModelShape,
  scenario: GlobalRelationshipsViewModelResetStreamShape['scenario'],
  loadedGraph: LoadedGraphWithViewpointMode,
  _modelUpdateReset: unknown,
  i18nLocale: I18nLocale
): GlobalRelationshipsViewModelResetStreamShape => ({
  context,
  components,
  scenarioRelatedComponentIds,
  graphModel,
  scenario,
  loadedGraph,
  i18nLocale,
});

/**
 * Shared, global streams witout parameters, that should trigger complete view model reset.
 * We prevent any emission if the global app state is currently updating
 */
export const globalViewModelResetSource$: Observable<GlobalRelationshipsViewModelResetStreamShape> =
  combineLatest([
    isUpdatingAppState$,
    context$,
    components$,
    scenarioRelatedComponentIds$,
    graphModel$,
    scenario$,
    loadedGraph$,
    relationshipsDiagramModelUpdateReset$,
    i18nLocale$,
  ]).pipe(
    filter(isNotUpdatingAppState),
    spreadMap(toGlobalViewModelResetPayload)
  );

export type SharedBlockDiagramsRenderingDataSource = {
  isContextChange: boolean;
  clearEdges: boolean;
  scenarioName: string;
  isViewpointMode: boolean;
};
export const sharedBlockDiagramsRenderingDataSource$: Observable<SharedBlockDiagramsRenderingDataSource> =
  combineLatest([
    isUpdatingAppState$,
    layoutUpdate$,
    scenarios$,
    activeScenario$,
    models$,
  ]).pipe(
    filter(([isUpdatingAppState]) => !isUpdatingAppState),
    map(([_, layoutUpdate, scenarios, scenarioId]) => ({
      isContextChange: layoutUpdate.isContextChange,
      clearEdges: layoutUpdate.referenceOrderChanged,
      scenarioName: scenarios.byId[scenarioId.scenarioId || '']?.name,
      isViewpointMode: true,
    })),
    debounceTime(100),
    distinctUntilChanged(isEqual)
  );

export const allActiveOrgUsers$ = orgUsers$.pipe(
  map(({ users }) => usersOperations.getAllOrgUsers(users)),
  distinctUntilChanged(isEqual)
);

export { orgUsersById$ } from 'streams/orgUsers/orgUsers$';
